import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import HistoryIcon from "@mui/icons-material/History";
import useFetch from "../../../../hooks/useFetch";
import { timeDifference } from "../../../../utils/timeDifference";
import {
    PageHeader,
    EmptyState,
    Skeleton,
} from "../../../shares/dashboard/DashboardWidgets";
import CurrentHW from "./cards/CurrentHW";
import PreviousHW from "./cards/PreviousHW";

/**
 * Assignments.
 *
 * The split predicate is unchanged (an assignment is open while its end date is
 * not in the past), but the memo now produces plain data instead of JSX
 * elements - building components inside useMemo meant the list could not
 * re-render for anything except a change of assignmentInfo.
 *
 * The previous-assignments column also had a hardcoded h-[63vh] with its own
 * scrollbar, which on a short window left a scroll area a few rows tall inside
 * an already-scrolling page. It scrolls with the page now.
 */
function Homeworks() {
    const [assignmentInfo, setAssignmentInfo] = useState();

    const examsFetchData = useFetch({
        method: "GET",
    safeRead: true,
        url: `assignment-platform/`,
        noHeader: false,
        trigger: true,
        setter: setAssignmentInfo,
        errMessage: () =>
            toast.error("مشکلی در دریافت اطلاعات تکالیف پیش آمده", {
                position: "bottom-left",
                rtl: true,
                autoClose: 2000,
            }),
    });

    const { current, previous } = useMemo(() => {
        const open = [];
        const closed = [];

        (Array.isArray(assignmentInfo) ? assignmentInfo : []).forEach((info) => {
            const isOpen =
                timeDifference(info.assignment_available_time_end, Date.now()) >= 0;
            (isOpen ? open : closed).push(info);
        });

        return { current: open, previous: closed };
    }, [assignmentInfo]);

    const isLoading = examsFetchData.loading && !assignmentInfo;

    return (
        <div className="flex flex-col gap-2xl pb-4xl">
            <PageHeader
                title="تکالیف"
                description="تکالیف باز و سابقه تحویل‌هایت"
                icon={DriveFileRenameOutlineIcon}
            />

            <section className="flex flex-col gap-md">
                <h2 className="text-md font-semibold text-ink">تکالیف باز</h2>
                {isLoading ? (
                    <Skeleton className="h-[18rem] w-full" />
                ) : current.length === 0 ? (
                    <EmptyState
                        icon={TaskAltIcon}
                        title="تکلیف بازی نداری"
                        description="همه‌چیز تحویل شده. تکلیف بعدی که باز بشه اینجا می‌آید."
                    />
                ) : (
                    <div className="flex flex-col gap-md">
                        {current.map((info) => (
                            <CurrentHW
                                key={`current-${info.id}`}
                                assignmentInfo={info}
                                examsFetchData={examsFetchData.reFetch}
                            />
                        ))}
                    </div>
                )}
            </section>

            <section className="flex flex-col gap-md">
                <h2 className="text-md font-semibold text-ink">تکالیف قبلی</h2>
                {isLoading ? (
                    <div className="flex flex-col gap-md">
                        {[0, 1].map((key) => (
                            <Skeleton key={key} className="h-[14rem] w-full" />
                        ))}
                    </div>
                ) : previous.length === 0 ? (
                    <EmptyState
                        icon={HistoryIcon}
                        title="هنوز تکلیفی تحویل نداده‌ای"
                        description="نمره و برگه تصحیح‌شده تکالیف اینجا بایگانی می‌شود."
                    />
                ) : (
                    <div className="flex flex-col gap-md">
                        {previous.map((info) => (
                            <PreviousHW key={`prev-${info.id}`} assignmentInfo={info} />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}

export default Homeworks;
